/**
 * @file `<ChapterFooter/>` — Previous / Up Next chapter navigation.
 * @module @labcraft/course-kit/components/chapter-footer
 *
 * Data-driven: the platform passes `prev` and `next` links derived
 * from the CourseRegistry's `getNeighbors(ref)` helper. Either may be
 * `null` for the first / last chapter; the corresponding button is
 * **omitted** (a disabled placeholder button with no destination reads
 * as broken chrome). When both are null — a single-chapter course —
 * the footer renders nothing.
 *
 * A platform may instead hand a **boundary link back to the course
 * landing** (carrying its own `dir` label, e.g. "Back to") for the
 * first chapter's Previous / last chapter's Up-next, so no chapter
 * dead-ends. `null` still omits the button where a platform prefers
 * that.
 *
 * The Next button can be **gated** (`nextLocked`): it stays visible but
 * renders disabled with a reason line, so a learner can't advance past
 * the chapter's exercises. This is deliberately disabled-with-reason,
 * not omitted — an absent Next reads as "end of course", a locked Next
 * reads as "not yet". The gate policy lives with the platform (lab
 * decides when the chapter's objectives + quiz are cleared); course-kit
 * only renders the state it's handed.
 *
 * The platform decides how navigation happens (router push, full
 * page load, …) — course-kit only invokes `onNavigate(href)`.
 */

import type { JSX } from 'react'
import { ChevronRightIcon, LinesIcon, LockIcon } from './icons.tsx'
import styles from './chapter_footer.module.css'

export interface ChapterNavLink {
  /** URL to navigate to — platform-defined shape (e.g. `/learn/...`). */
  href: string
  /** Chapter title — or course title, for a boundary link — to show. */
  title: string
  /**
   * Direction word above the title. Defaults to "Previous" (`prev`) /
   * "Up next" (`next`); a boundary link back to the course overrides it
   * (e.g. "Back to").
   */
  dir?: string
}

export interface ChapterFooterProps {
  prev: ChapterNavLink | null
  next: ChapterNavLink | null
  onNavigate: (href: string) => void
  /**
   * Gate the Next button: it stays visible but renders disabled and
   * `onNavigate` is not invoked. The platform sets this while the
   * chapter's exercises (objectives + quiz) aren't cleared. Ignored
   * when `next` is a boundary link back to the course (a wrap, never a
   * forward advance) — those are never gated. Defaults to `false`.
   */
  nextLocked?: boolean
  /**
   * Reason shown under the Next title while `nextLocked` — e.g. "Pass
   * the knowledge check to continue". Also the button's `title`. Ignored
   * when not locked.
   */
  nextLockedReason?: string
  /**
   * Umbrella #1446 Part D — when provided, render a glossary affordance
   * BETWEEN Previous and Next that opens the course-wide glossary. The
   * platform supplies it only on `lesson`-role chapters (an opening or
   * closing, and a role-less pre-conformance chapter, pass `undefined`),
   * so course-kit renders the button iff handed the handler — it owns no
   * role logic itself.
   */
  onGlossary?: () => void
}

export function ChapterFooter(props: ChapterFooterProps): JSX.Element | null {
  // Nothing to offer — no navigation and no glossary affordance — so the
  // footer has nothing to render.
  if (!props.prev && !props.next && !props.onGlossary) return null

  const { prev, next, onGlossary, nextLocked = false, nextLockedReason } = props
  return (
    <footer className={styles.footer}>
      {prev && (
        <button
          className={styles.btn}
          type="button"
          data-testid="chapter-prev"
          onClick={() => props.onNavigate(prev.href)}
          title={prev.title}>
          <span
            className={styles.chevron}
            style={{ transform: 'rotate(180deg)' }}>
            <ChevronRightIcon />
          </span>
          <span className={styles.col}>
            <span className={styles.dir}>{prev.dir ?? 'Previous'}</span>
            <span className={styles.title}>{prev.title}</span>
          </span>
        </button>
      )}
      {onGlossary && (
        <button
          className={styles.btnGlossary}
          type="button"
          data-testid="chapter-glossary"
          onClick={onGlossary}
          title="Open the course glossary">
          <LinesIcon className={styles.glossaryIcon} width={15} height={15} />
          <span className={styles.glossaryLabel}>Glossary</span>
        </button>
      )}
      {next && (
        <button
          className={`${styles.btn} ${styles.btnNext}${
            nextLocked ? ` ${styles.locked}` : ''
          }`}
          type="button"
          data-testid="chapter-next"
          disabled={nextLocked}
          aria-disabled={nextLocked || undefined}
          onClick={() => {
            if (nextLocked) return
            props.onNavigate(next.href)
          }}
          title={nextLocked ? (nextLockedReason ?? next.title) : next.title}>
          <span className={`${styles.col} ${styles.colRight}`}>
            <span className={styles.dir}>{next.dir ?? 'Up next'}</span>
            <span className={styles.title}>{next.title}</span>
            {nextLocked && nextLockedReason && (
              <span className={styles.reason}>
                <LockIcon
                  className={styles.reasonIcon}
                  width={11}
                  height={11}
                />
                {nextLockedReason}
              </span>
            )}
          </span>
          <span className={styles.chevron}>
            <ChevronRightIcon />
          </span>
        </button>
      )}
    </footer>
  )
}
